/**
 * discovery_ui.ts — the words the survey desk prints (space campaign slice F, 2026-09-30). Pure: no DOM, no
 * connection — the panel, the HUD and the harnesses read the same lines. The voice is atlas/style.md §13: short,
 * plain, dry. Every action line says what happens, what it costs and what changes.
 */
import {
  SITE_LABEL, type SiteKind, RELAY_UNITS, LEASE_S, leaseBondSov, hazardBand, DAMAGE_AT, DAMAGED_PAY_PCT,
  POST_CHANNELS, INTEL_SCOPE_LABEL, type IntelScope, LATE_HANDIN_PCT,
} from '@shared/space_survey.js';
import { sysTierOf } from '@shared/space_industry.js';

export function ageText(ms: number): string {
  if (!(ms >= 0)) return 'unknown';
  const s = Math.floor(ms / 1000);
  if (s < 90) return `${s} s ago`;
  if (s < 5400) return `${Math.round(s / 60)} min ago`;
  return `${(s / 3600).toFixed(1)} h ago`;
}
export function untilText(ms: number): string {
  if (ms <= 0) return 'now';
  const s = Math.floor(ms / 1000);
  if (s < 90) return `${s} s`;
  if (s < 5400) return `${Math.round(s / 60)} min`;
  return `${(s / 3600).toFixed(1)} h`;
}

export interface SiteFacts {
  sigKey: string; systemId: string; kind: string; stage: string; state: string; stock: number; hazard: number;
  band: string; knownAtMs: number; source: string; leasedBy: string; leaseExpiresMs: number; recoverUntilMs: number; firstByMe: boolean;
}
/** One line for a site I know: what it is, what I know of it and how old that knowledge is, its status. */
export function siteLine(f: SiteFacts, nowMs: number, systemName: string): string {
  const kind = SITE_LABEL[f.kind as SiteKind] ?? f.kind;
  const parts = [`${kind} · ${systemName}${f.firstByMe ? ' · first found by you' : ''}`];
  if (f.stage === 'assessed') parts.push(`hazard ${f.hazard} (${f.band || hazardBand(f.hazard)})${f.kind === 'vault' ? ` · ${f.stock} u left` : f.stock > 0 ? ' · intact stock' : ' · taken'} · assessed ${ageText(nowMs - f.knownAtMs)}${f.source === 'licence' ? ' (licensed)' : ''}`);
  else if (f.band) parts.push(`hazard ${f.band} (${f.source === 'census' ? 'desk census' : 'licensed report'}, ${ageText(nowMs - f.knownAtMs)}) · not assessed`);
  else parts.push('observed — not assessed (hold the beam at it)');
  const status = f.leasedBy === 'me' ? `YOUR lease — ${untilText(f.leaseExpiresMs - nowMs)} left` : f.leasedBy === 'other' ? 'leased by another pilot' : f.state;
  parts.push(`${status} · recoverable for ${untilText(f.recoverUntilMs - nowMs)}`);
  return parts.join(' · ');
}

/** The lease offer: what it reserves, what it costs, what ends it. */
export function leaseTerms(kind: string, systemId: string, onTender: boolean): string {
  const reserve = kind === 'relay' ? `${RELAY_UNITS} u of your hold` : kind === 'vault' ? 'the hold space you have free (≤ 24 u)' : 'no hold space (a component rides in your inventory)';
  const pay = onTender ? 'funded by your tender (no bond)' : `a ${leaseBondSov(sysTierOf(systemId))} ⓒ bond, back when you extract or release, lost if the lease runs out`;
  return `Lease for ${Math.round(LEASE_S / 60)} min: reserves ${reserve}; ${pay}. Offline over 2 min or a hull change frees it to the next pilot — your completed work stays with the site.`;
}

export interface TenderFacts { tenderId: bigint; postName: string; systemId: string; rewardSov: number; state: string; deadlineMs: number; acceptedByMe: boolean; band: string }
/** The terms a pilot reads BEFORE accepting (the report §5A: funding, cargo size, route, risk — no seat). */
export function tenderTerms(t: TenderFacts, nowMs: number, systemName: string): string[] {
  return [
    `${t.postName} pays ${t.rewardSov} ⓒ from escrow (held now) for one survey relay from ${systemName} (tier ${sysTierOf(t.systemId)}).`,
    `Cargo: ${RELAY_UNITS} u. The site is not named here — resolve it with your scanner in ${systemName}.`,
    `Risk: ${t.band ? `the hazard reads ${t.band}` : 'the hazard is unassessed'} — a relay pulled at hazard ${DAMAGE_AT}+ comes out damaged and earns ${DAMAGED_PAY_PCT} %; stabilizing first costs crystal.`,
    `Deliver here by ${untilText(t.deadlineMs - nowMs)} from now. Hull insurance does not cover the relay; a lost hull leaves it in the wreck.`,
  ];
}

export function postLine(name: string, channelsLit: number, balance: number | null, relays: number, status: string): string {
  return `${name}: survey array ${channelsLit}/${POST_CHANNELS} channels lit · budget ${balance === null ? 'unknown' : `${balance} ⓒ`} · ${relays} relay${relays === 1 ? '' : 's'} received${status ? ` · ${status}` : ''}`;
}
export function handInLine(systemId: string): string {
  return `Hand in a relay with no tender: ${LATE_HANDIN_PCT} % of a tender (${Math.floor(120 * sysTierOf(systemId) * LATE_HANDIN_PCT / 100)} ⓒ here) if the desk can pay — "unfunded" otherwise.`;
}
export function offerLine(scope: string, systemName: string, observedMs: number, nowMs: number, evidence: string, price: number): string {
  return `${INTEL_SCOPE_LABEL[scope as IntelScope] ?? scope} · ${systemName} · observed ${ageText(nowMs - observedMs)} (${evidence}) · ${price} ⓒ`;
}
